import React, { memo, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Block as BlockIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  HowToReg as HowToRegIcon,
  Build as BuildIcon,
  SwapHoriz as RoomChangeIcon,
  TrendingUp as RoomUpgradeIcon,
  TrendingDown as RoomDowngradeIcon,
  MeetingRoomOutlined as RoomIcon,
  EventAvailable as StayIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import moment from 'moment';
import {
  getStatusColors,
  getRoomTypeChangeLabel,
  getRoomTypeChangeMessage,
} from '../../utils/bookingUtils';
import { calculateBarPosition, formatHotelDateTime } from '../../utils/dateUtils';
import { getStaticFileUrl, guessOtaStaticPath, resolveChannelImageUrl } from '../../utils/staticUtils';
import { getUltraRoomMenuButtonSx } from '../../utils/dashboardUltraGridLayout';
import { resolveRoomMaintenanceSummary } from '../../utils/maintenanceRoomUtils';
import {
  getRoomMoveHoverBookingId,
  scheduleClearRoomMoveHoverBookingId,
  setRoomMoveHoverBookingId,
  subscribeRoomMoveHover,
} from '../../utils/roomMoveHoverStore';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  getStayDurationMeta,
  resolveActualCheckInDisplay,
  resolveActualCheckOutDisplay,
  resolveStayCheckInDisplay,
  resolveStayCheckOutDisplay,
} from '../../utils/bookingStayDisplay';
import ChannelImage from '../common/ChannelImage';

const WEB_CHECKIN_ACCENT = DASHBOARD_ACCENT;

function formatWebCheckInCountdown(expiresAt, nowMs = Date.now()) {
  if (!expiresAt) return '';
  const end = moment(expiresAt);
  if (!end.isValid()) return '';
  const remainingMs = end.valueOf() - nowMs;
  if (remainingMs <= 0) return 'Expired';
  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Channel / PMS image (drawer logic: path when set, else PMS asset).
 */
function BookingSourceLogo({ channelImagePath, channelName, isDarkMode, sizePx }) {
  const [failed, setFailed] = useState(false);
  const [useNameFallback, setUseNameFallback] = useState(false);

  const channel = {
    image_path: channelImagePath,
    name: channelName,
  };
  const primarySrc = resolveChannelImageUrl(channel);
  const guessed = guessOtaStaticPath(channelName);
  const nameFallbackSrc = guessed ? getStaticFileUrl(guessed) : getStaticFileUrl('ota/pms.png');
  const src = failed
    ? nameFallbackSrc
    : (useNameFallback ? nameFallbackSrc : (primarySrc || nameFallbackSrc));

  return (
    <Box
      component="img"
      src={src}
      alt={channelName || 'Booking source'}
      onError={() => {
        if (!useNameFallback && nameFallbackSrc && nameFallbackSrc !== src) {
          setUseNameFallback(true);
          return;
        }
        setFailed(true);
      }}
      sx={{
        width: sizePx,
        height: sizePx,
        objectFit: 'contain',
        display: 'block',
        flexShrink: 0,
        filter: isDarkMode ? 'contrast(1.06) brightness(1.05)' : 'none',
      }}
    />
  );
}

function resolveBarActionEligibility(status, { onCheckIn, onCheckout } = {}) {
  const s = String(status || '').toLowerCase();
  const checkInEligible =
    Boolean(onCheckIn)
    && ['booked', 'confirmed', 'reserved', 'pending'].includes(s);
  const checkoutEligible =
    Boolean(onCheckout)
    && ['checked_in', 'partial_checkin', 'partial_checkout'].includes(s);
  return { checkInEligible, checkoutEligible };
}

function humanizeStatus(status) {
  const s = String(status || '').trim().replace(/_/g, ' ');
  if (!s) return '—';
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTooltipDateTime(value, hotelTimezone) {
  if (!value) return null;
  if (moment.isMoment(value)) {
    return value.isValid() ? formatHotelDateTime(value.toISOString(), hotelTimezone) : null;
  }
  const formatted = formatHotelDateTime(value, hotelTimezone);
  if (formatted) return formatted;
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY hh:mm A') : null;
}

function formatStayDateLine(display) {
  if (!display?.date && !display?.time) return null;
  if (display.date && display.time) return `${display.date} · ${display.time}`;
  return display.date || display.time || null;
}

/** Label for room-change tooltips from segment neighbors (not the current room alone). */
function formatRoomMoveLabel(roomMove) {
  if (!roomMove) return 'Guest moved between rooms';
  const from = roomMove.fromRoomNumber;
  const to = roomMove.toRoomNumber;
  const current = roomMove.currentRoomNumber;
  // Arriving segment after a move: 102 → 301
  if (from && current) return `Room ${from} → ${current}`;
  // Departing segment before a move: Moved to 301
  if (to) return `Moved to Room ${to}`;
  // Fallback when only one side is known
  if (from && to) return `Room ${from} → ${to}`;
  if (from) return `Moved from Room ${from}`;
  return 'Guest moved between rooms';
}

/** Deluxe → Premium chips for upgrade / downgrade tooltips. */
function RoomTypeChangeFlowChips({ fromName, toName, kind = 'upgrade', isDarkMode = true }) {
  const from = String(fromName || '').trim();
  const to = String(toName || '').trim();
  if (!from || !to) {
    return (
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35 }}>
        {getRoomTypeChangeMessage(kind)}
      </Typography>
    );
  }
  const accent = kind === 'downgrade' ? '#f59e0b' : '#38bdf8';
  const chipBaseSx = {
    height: 22,
    maxWidth: 140,
    fontWeight: 700,
    fontSize: '0.68rem',
    borderRadius: '8px',
    '& .MuiChip-label': {
      px: 0.85,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.55,
        flexWrap: 'wrap',
        mt: 0.15,
      }}
    >
      <Chip
        size="small"
        label={from}
        sx={{
          ...chipBaseSx,
          bgcolor: isDarkMode ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1),
          color: isDarkMode ? alpha('#e2e8f0', 0.92) : '#334155',
          border: `1px solid ${isDarkMode ? alpha('#94a3b8', 0.28) : alpha('#64748b', 0.22)}`,
        }}
      />
      <Typography
        component="span"
        sx={{ fontSize: '0.85rem', fontWeight: 800, color: accent, lineHeight: 1 }}
      >
        →
      </Typography>
      <Chip
        size="small"
        label={to}
        sx={{
          ...chipBaseSx,
          bgcolor: alpha(accent, isDarkMode ? 0.2 : 0.14),
          color: accent,
          border: `1px solid ${alpha(accent, isDarkMode ? 0.5 : 0.4)}`,
        }}
      />
    </Box>
  );
}

function barTooltipPaperSx(isDarkMode, accent) {
  return {
    bgcolor: isDarkMode ? alpha('#020617', 0.96) : alpha('#ffffff', 0.98),
    color: isDarkMode ? '#f1f5f9' : '#0f172a',
    border: `1px solid ${alpha(accent, isDarkMode ? 0.45 : 0.28)}`,
    borderRadius: '14px',
    boxShadow: isDarkMode
      ? `0 12px 40px ${alpha('#000', 0.55)}, 0 0 0 1px ${alpha('#fff', 0.04)}`
      : `0 12px 32px ${alpha('#0f172a', 0.14)}, 0 0 0 1px ${alpha('#0f172a', 0.04)}`,
    backdropFilter: 'blur(14px) saturate(1.2)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
    px: 1.5,
    py: 1.25,
    maxWidth: 320,
  };
}

function BookingInfoTooltipBody({
  bar,
  room,
  isDarkMode,
  accent,
  labelColor,
}) {
  const booking = bar.booking;
  if (!booking) return null;
  const tz = booking.hotel_timezone;
  const statusLabel = humanizeStatus(bar.status || booking.status);
  const code = booking.booking_code || booking.code || '';
  const roomNumber = room?.number
    || room?.room_number
    || room?.name
    || bar.roomMove?.currentRoomNumber;
  const stayMeta = getStayDurationMeta(booking);
  const stayIn = resolveStayCheckInDisplay(booking);
  const stayOut = resolveStayCheckOutDisplay(booking);
  const actualIn = resolveActualCheckInDisplay(booking);
  const actualOut = resolveActualCheckOutDisplay(booking);
  const checkInLine = formatStayDateLine(actualIn?.time ? actualIn : stayIn) || formatStayDateLine(stayIn);
  const checkOutLine = formatStayDateLine(actualOut?.time ? actualOut : stayOut) || formatStayDateLine(stayOut);
  const roomMove = bar.roomMove;
  const muted = isDarkMode ? alpha('#f8fafc', 0.62) : alpha('#0f172a', 0.55);
  const rowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1.5,
    alignItems: 'baseline',
    mb: 0.45,
  };

  const hasOtaChannel = Boolean(booking.channel_image_path || booking.channel_name);
  const sourceLabel = hasOtaChannel
    ? (booking.channel_name || 'Channel')
    : 'PMS';

  return (
    <Box sx={{ minWidth: 220 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '0.95rem',
          lineHeight: 1.25,
          mb: 0.15,
          color: isDarkMode ? '#f8fafc' : '#0f172a',
        }}
      >
        {booking.guest_name || 'Guest'}
      </Typography>
      {code ? (
        <Typography sx={{ fontSize: '0.72rem', color: muted, mb: 0.85, fontWeight: 600 }}>
          {code}
        </Typography>
      ) : (
        <Box sx={{ mb: 0.85 }} />
      )}

      {roomNumber ? (
        <Box sx={{ ...rowSx, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: muted }}>
            <RoomIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Room</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
            {roomNumber}
            {room?.room_type_name ? ` · ${room.room_type_name}` : ''}
          </Typography>
        </Box>
      ) : null}

      {checkInLine ? (
        <Box sx={rowSx}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: muted }}>Check-in</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'right' }}>
            {checkInLine}
          </Typography>
        </Box>
      ) : null}
      {checkOutLine ? (
        <Box sx={rowSx}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: muted }}>Check-out</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'right' }}>
            {checkOutLine}
          </Typography>
        </Box>
      ) : null}

      {(stayMeta?.bookedChipLabel || stayMeta?.stayChipLabel || stayMeta?.chipLabel) ? (
        <Box sx={{ ...rowSx, alignItems: 'center', mb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: muted }}>
            <StayIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Stay</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {stayMeta.bookedChipLabel ? (
              <Chip
                size="small"
                label={stayMeta.bookedChipLabel}
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: alpha('#14b8a6', isDarkMode ? 0.22 : 0.12),
                  color: isDarkMode ? '#5eead4' : '#0f766e',
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            ) : null}
            {(stayMeta.stayChipLabel || (!stayMeta.bookedChipLabel && stayMeta.chipLabel)) ? (
              <Chip
                size="small"
                label={stayMeta.stayChipLabel || stayMeta.chipLabel}
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: alpha(accent, isDarkMode ? 0.22 : 0.12),
                  color: labelColor,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}

      {roomMove && (roomMove.hasRoomChange || roomMove.hasRoomUpgrade) ? (
        <>
          <Divider sx={{ my: 1, borderColor: alpha(accent, isDarkMode ? 0.28 : 0.18) }} />
          {roomMove.hasRoomChange ? (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: roomMove.hasRoomUpgrade ? 0.75 : 0 }}>
              <RoomChangeIcon sx={{ fontSize: 16, color: '#fb923c', mt: '1px' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#fb923c' }}>
                  Room change
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.35 }}>
                  {formatRoomMoveLabel(roomMove)}
                </Typography>
                {formatTooltipDateTime(roomMove.changedAt || roomMove.segmentStartedAt, tz) ? (
                  <Typography sx={{ fontSize: '0.68rem', color: muted, mt: 0.2 }}>
                    {formatTooltipDateTime(roomMove.changedAt || roomMove.segmentStartedAt, tz)}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
          {roomMove.hasRoomUpgrade ? (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              {roomMove.roomTypeChangeKind === 'downgrade' ? (
                <RoomDowngradeIcon sx={{ fontSize: 16, color: '#f59e0b', mt: '1px' }} />
              ) : (
                <RoomUpgradeIcon sx={{ fontSize: 16, color: '#38bdf8', mt: '1px' }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    color: roomMove.roomTypeChangeKind === 'downgrade' ? '#f59e0b' : '#38bdf8',
                  }}
                >
                  {getRoomTypeChangeLabel(roomMove.roomTypeChangeKind)}
                </Typography>
                <RoomTypeChangeFlowChips
                  fromName={roomMove.fromRoomTypeName}
                  toName={roomMove.toRoomTypeName}
                  kind={roomMove.roomTypeChangeKind}
                  isDarkMode={isDarkMode}
                />
              </Box>
            </Box>
          ) : null}
        </>
      ) : null}

      <Divider sx={{ my: 1, borderColor: alpha(accent, isDarkMode ? 0.28 : 0.18) }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Chip
          size="small"
          label={statusLabel}
          sx={{
            height: 22,
            fontSize: '0.68rem',
            fontWeight: 700,
            bgcolor: alpha(accent, isDarkMode ? 0.22 : 0.14),
            color: labelColor,
            border: `1px solid ${alpha(accent, isDarkMode ? 0.4 : 0.28)}`,
            '& .MuiChip-label': { px: 0.85 },
          }}
        />
        <Box
          title={sourceLabel}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: '8px',
            flexShrink: 0,
            bgcolor: isDarkMode ? alpha('#fff', 0.08) : alpha('#0f172a', 0.04),
            border: `1px solid ${isDarkMode ? alpha('#fff', 0.14) : alpha('#0f172a', 0.1)}`,
            overflow: 'hidden',
          }}
        >
          {hasOtaChannel ? (
            <ChannelImage
              channel={{
                image_path: booking.channel_image_path,
                name: booking.channel_name,
              }}
              size={16}
            />
          ) : (
            <ChannelImage isPMS size={16} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function RoomMoveIconTooltip({ title, children, isDarkMode, accent }) {
  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      enterDelay={200}
      describeChild
      slotProps={{
        tooltip: { sx: barTooltipPaperSx(isDarkMode, accent) },
        arrow: { sx: { color: isDarkMode ? alpha('#020617', 0.96) : alpha('#ffffff', 0.98) } },
      }}
    >
      {children}
    </Tooltip>
  );
}

/**
 * BookingBar Component
 * Reusable component for rendering booking bars in the calendar grid
 */
const BookingBar = ({
  bar,
  room = null,
  dateRangeLength,
  isDarkMode,
  roomColumnWidth = 0,
  laneIndex = 0,
  barHeight = 40,
  barGap = 8,
  barTopOffset = 8,
  layoutVariant = 'default',
  barMetrics = null,
  onClick,
  onViewBooking,
  onOpenWebCheckInInvite,
  onCheckIn,
  onCheckout,
}) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [suppressBarTooltip, setSuppressBarTooltip] = useState(false);
  const [barTooltipOpen, setBarTooltipOpen] = useState(false);
  const menuOpen = Boolean(menuAnchor);
  const isWorkspace = layoutVariant === 'workspace';
  const colors = getStatusColors(bar.status, isDarkMode);
  const statusLower = String(bar.status || '').toLowerCase();
  const isCancelledLike =
    statusLower === 'cancelled' || statusLower === 'canceled' || statusLower === 'no_show';
  const isBlockBar = Boolean(bar.blockInfo) || statusLower === 'blocked';
  const isWebCheckInHold = Boolean(bar.webCheckInInvite?.id) || statusLower === 'web_checkin';
  const isClickable = isBlockBar
    ? false
    : isWebCheckInHold
      ? Boolean(onOpenWebCheckInInvite && bar.webCheckInInvite)
      : !!bar.booking;
  const bookingId = bar.booking?.id || bar.booking?.booking_id;
  const isGroupMember = Boolean(
    bar.booking?.group_booking_id
    || bar.booking?.group_booking_code
    || bar.booking?.is_group_member
  );
  const groupBadgeTitle = bar.booking?.group_booking_code
    ? `Group ${bar.booking.group_booking_code}`
    : 'Group booking';
  const roomMove = bar.roomMove || null;
  const showRoomChangeIcon = Boolean(roomMove?.hasRoomChange);
  const showRoomUpgradeIcon = Boolean(roomMove?.hasRoomUpgrade);
  const hasRoomMoveLink = showRoomChangeIcon || showRoomUpgradeIcon;
  const roomMoveBookingIdNum = hasRoomMoveLink ? (Number(bookingId) || 0) : 0;
  // Snapshot is a boolean so non-linked bars stay false and skip hover re-renders.
  const isRoomMoveHighlighted = useSyncExternalStore(
    subscribeRoomMoveHover,
    () => (
      roomMoveBookingIdNum > 0
      && getRoomMoveHoverBookingId() === roomMoveBookingIdNum
    ),
    () => false,
  );
  const { checkInEligible, checkoutEligible } = resolveBarActionEligibility(bar.status, {
    onCheckIn,
    onCheckout,
  });
  const hasMaintenance = Boolean(resolveRoomMaintenanceSummary(room).hasMaintenance);
  const showWebCheckInMenu = isWebCheckInHold && Boolean(onOpenWebCheckInInvite && bar.webCheckInInvite);
  const showActionsMenu = !isBlockBar && (
    showWebCheckInMenu
    || hasMaintenance
    || (
      Boolean(bar.booking) && (
        Boolean(onViewBooking || onClick)
        || checkInEligible
        || checkoutEligible
      )
    )
  );

  // Only tick countdown while the actions menu is open — avoids N×1s timers on large boards.
  useEffect(() => {
    if (!menuOpen || !isWebCheckInHold || !bar.webCheckInInvite?.expires_at) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [menuOpen, isWebCheckInHold, bar.webCheckInInvite?.expires_at]);

  const webCheckInCountdown = useMemo(
    () => (isWebCheckInHold ? formatWebCheckInCountdown(bar.webCheckInInvite?.expires_at, nowMs) : ''),
    [isWebCheckInHold, bar.webCheckInInvite?.expires_at, nowMs],
  );
  const topOffset = barTopOffset + laneIndex * (barHeight + barGap);
  // Keep logo compact so icon + name + menu stay on one row inside the pill.
  const logoSizePx = Math.min(
    barMetrics?.logoSize ?? Math.round(barHeight * 0.55),
    Math.max(14, barHeight - 12),
  );
  const labelFontSize = barMetrics?.labelFontSize ?? (isWorkspace ? '0.74rem' : '0.78rem');
  const pillBorderRadius = barMetrics?.pillBorderRadius ?? (isWorkspace ? '12px' : 9999);
  // Material-style translucent glass bars (status tint + soft hairline, no glow outline).
  const accent = colors.fill || colors.line || (isDarkMode ? '#f08c00' : '#e67700');
  const labelColor = isDarkMode
    ? (colors.line || '#fff')
    : (colors.fill || colors.line || '#0f172a');
  const chipSurface = isDarkMode ? THEME_COLORS.BACKGROUND_DARK : '#fff';
  const chipBg = isWorkspace
    ? (isDarkMode
      ? `linear-gradient(135deg, ${alpha(accent, 0.52)} 0%, ${alpha(accent, 0.36)} 100%), ${chipSurface}`
      : `linear-gradient(135deg, ${alpha(accent, 0.42)} 0%, ${alpha(accent, 0.26)} 100%), ${chipSurface}`)
    : (isDarkMode
      ? `linear-gradient(135deg, ${alpha(accent, 0.34)} 0%, ${alpha(accent, 0.18)} 100%)`
      : `linear-gradient(135deg, ${alpha(accent, 0.22)} 0%, ${alpha(accent, 0.12)} 100%)`);
  const chipBorder = `1px solid ${alpha(accent, isDarkMode ? 0.42 : 0.36)}`;
  const chipShadow = isDarkMode
    ? `inset 0 1px 0 ${alpha('#fff', 0.12)}, 0 4px 14px ${alpha('#000', 0.28)}`
    : `inset 0 1px 0 ${alpha('#fff', 0.55)}, 0 2px 8px ${alpha(accent, 0.12)}`;
  const chipShadowHover = isDarkMode
    ? `inset 0 1px 0 ${alpha('#fff', 0.16)}, 0 6px 18px ${alpha('#000', 0.36)}, 0 0 0 1px ${alpha(accent, 0.22)}`
    : `inset 0 1px 0 ${alpha('#fff', 0.7)}, 0 4px 14px ${alpha(accent, 0.18)}, 0 0 0 1px ${alpha(accent, 0.2)}`;
  const chipBgHover = isWorkspace
    ? (isDarkMode
      ? `linear-gradient(135deg, ${alpha(accent, 0.62)} 0%, ${alpha(accent, 0.44)} 100%), ${chipSurface}`
      : `linear-gradient(135deg, ${alpha(accent, 0.5)} 0%, ${alpha(accent, 0.32)} 100%), ${chipSurface}`)
    : (isDarkMode
      ? `linear-gradient(135deg, ${alpha(accent, 0.44)} 0%, ${alpha(accent, 0.26)} 100%)`
      : `linear-gradient(135deg, ${alpha(accent, 0.3)} 0%, ${alpha(accent, 0.16)} 100%)`);
  const hideActionsWhenSpanAtMost = barMetrics?.hideActionsWhenSpanAtMost ?? 0;
  const showMenuButton = showActionsMenu && bar.span > hideActionsWhenSpanAtMost;
  const isRoomTypeDowngrade = roomMove?.roomTypeChangeKind === 'downgrade';
  const roomTypeChangeAccent = isRoomTypeDowngrade ? '#f59e0b' : '#38bdf8';
  const hotelTz = bar.booking?.hotel_timezone;

  const { leftOffset, barWidth } = calculateBarPosition(
    bar.startCol,
    bar.span,
    dateRangeLength,
    bar.startsBeforeCalendar,
    bar.extendsBeyondCalendar || false,
    roomColumnWidth,
    bar.startDayFraction ?? null,
    bar.endDayFraction ?? null,
  );

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const openViewBooking = () => {
    if (onViewBooking) {
      onViewBooking(bookingId);
      return;
    }
    if (onClick && bookingId) onClick(bookingId);
  };

  const openWebCheckInInvite = () => {
    if (onOpenWebCheckInInvite && bar.webCheckInInvite) {
      onOpenWebCheckInInvite(bar.webCheckInInvite);
    }
  };

  const menuButtonSx = {
    ...getUltraRoomMenuButtonSx(isDarkMode),
    width: Math.max(20, Math.min(26, barHeight - 10)),
    height: Math.max(20, Math.min(26, barHeight - 10)),
    flexShrink: 0,
    color: labelColor,
    borderColor: alpha(accent, isDarkMode ? 0.35 : 0.28),
    bgcolor: alpha(accent, isDarkMode ? 0.14 : 0.1),
    '&:hover': {
      bgcolor: alpha(accent, isDarkMode ? 0.24 : 0.18),
      borderColor: alpha(accent, isDarkMode ? 0.5 : 0.4),
    },
  };

  const moveIconBtnSx = (tone) => ({
    width: Math.max(16, Math.min(20, barHeight - 14)),
    height: Math.max(16, Math.min(20, barHeight - 14)),
    p: 0,
    flexShrink: 0,
    color: tone,
    bgcolor: alpha(tone, isDarkMode ? 0.2 : 0.12),
    border: `1px solid ${alpha(tone, isDarkMode ? 0.45 : 0.32)}`,
    borderRadius: '999px',
    '&:hover': {
      bgcolor: alpha(tone, isDarkMode ? 0.32 : 0.2),
    },
  });

  // Defer heavy tooltip trees until open — avoids building them for every bar on each render.
  const bookingTooltipTitle = !barTooltipOpen
    ? (bar.booking || isWebCheckInHold || isBlockBar ? ' ' : '')
    : bar.booking ? (
      <BookingInfoTooltipBody
        bar={bar}
        room={room}
        isDarkMode={isDarkMode}
        accent={accent}
        labelColor={labelColor}
      />
    ) : isWebCheckInHold ? (
      <Box sx={{ minWidth: 180 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: accent, mb: 0.5 }}>
          Web Check-in Hold
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
          {bar.webCheckInInvite?.guest_name || 'Guest'}
        </Typography>
        {webCheckInCountdown ? (
          <Typography sx={{ fontSize: '0.72rem', mt: 0.5, opacity: 0.75 }}>
            Expires {webCheckInCountdown}
          </Typography>
        ) : null}
      </Box>
    ) : isBlockBar ? (
      <Box sx={{ minWidth: 160 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: accent, mb: 0.5 }}>
          Inventory Block
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
          {bar.blockInfo?.reason || 'Blocked'}
        </Typography>
      </Box>
    ) : '';

  const roomChangeTooltipTitle = showRoomChangeIcon ? (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#fb923c', mb: 0.35 }}>
        Room change
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
        {formatRoomMoveLabel(roomMove)}
      </Typography>
      {formatTooltipDateTime(roomMove?.changedAt || roomMove?.segmentStartedAt, hotelTz) ? (
        <Typography sx={{ fontSize: '0.68rem', mt: 0.35, opacity: 0.75 }}>
          {formatTooltipDateTime(roomMove?.changedAt || roomMove?.segmentStartedAt, hotelTz)}
        </Typography>
      ) : null}
    </Box>
  ) : null;

  const roomUpgradeTooltipTitle = showRoomUpgradeIcon ? (
    <Box sx={{ minWidth: 160 }}>
      <Typography
        sx={{ fontWeight: 800, fontSize: '0.75rem', color: roomTypeChangeAccent, mb: 0.35 }}
      >
        {getRoomTypeChangeLabel(roomMove?.roomTypeChangeKind)}
      </Typography>
      <RoomTypeChangeFlowChips
        fromName={roomMove?.fromRoomTypeName}
        toName={roomMove?.toRoomTypeName}
        kind={roomMove?.roomTypeChangeKind}
        isDarkMode={isDarkMode}
      />
    </Box>
  ) : null;

  const barNode = (
      <Box
        data-room-move-booking={hasRoomMoveLink && bookingId ? String(bookingId) : undefined}
        data-room-move-upgrade={showRoomUpgradeIcon ? '1' : undefined}
        data-room-id={room?.id != null ? String(room.id) : undefined}
        onMouseEnter={() => {
          if (hasRoomMoveLink && bookingId) {
            setRoomMoveHoverBookingId(bookingId);
          }
        }}
        onMouseLeave={() => {
          if (hasRoomMoveLink) {
            scheduleClearRoomMoveHoverBookingId(120);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isWebCheckInHold && onOpenWebCheckInInvite) {
            onOpenWebCheckInInvite(bar.webCheckInInvite);
            return;
          }
          if (isClickable && onClick) onClick(bookingId);
        }}
        sx={{
          position: 'absolute',
          left: leftOffset,
          top: `${topOffset}px`,
          width: barWidth,
          height: `${barHeight}px`,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), filter 0.2s ease',
          zIndex: isRoomMoveHighlighted ? 14 : (5 + laneIndex),
          overflow: isRoomMoveHighlighted ? 'visible' : 'hidden',
          borderRadius: pillBorderRadius,
          transform: isRoomMoveHighlighted ? 'translateY(-3px) scale(1.035)' : 'none',
          filter: isRoomMoveHighlighted
            ? `drop-shadow(0 10px 18px ${alpha(DASHBOARD_ACCENT, isDarkMode ? 0.42 : 0.28)})`
            : 'none',
          '& .booking-bar-pill': isRoomMoveHighlighted
            ? {
                borderColor: alpha(DASHBOARD_ACCENT, isDarkMode ? 0.95 : 0.85),
                boxShadow: isDarkMode
                  ? `0 1px 2px ${alpha('#000', 0.45)}, 0 8px 18px ${alpha('#000', 0.38)}, 0 0 0 1.5px ${alpha(DASHBOARD_ACCENT, 0.9)}, inset 0 1px 0 ${alpha('#fff', 0.14)}`
                  : `0 1px 2px ${alpha('#0f172a', 0.08)}, 0 10px 22px ${alpha(DASHBOARD_ACCENT, 0.28)}, 0 0 0 1.5px ${alpha(DASHBOARD_ACCENT, 0.75)}, inset 0 1px 0 ${alpha('#fff', 0.55)}`,
              }
            : {},
          '&:hover': isClickable
            ? {
                transform: isRoomMoveHighlighted
                  ? 'translateY(-3px) scale(1.035)'
                  : 'translateY(-1px)',
                zIndex: isRoomMoveHighlighted ? 15 : 6,
                '& .booking-bar-pill': {
                  background: chipBgHover,
                  boxShadow: isRoomMoveHighlighted
                    ? (isDarkMode
                      ? `0 1px 2px ${alpha('#000', 0.45)}, 0 10px 22px ${alpha('#000', 0.42)}, 0 0 0 1.5px ${alpha(DASHBOARD_ACCENT, 0.95)}, inset 0 1px 0 ${alpha('#fff', 0.16)}`
                      : `0 1px 2px ${alpha('#0f172a', 0.1)}, 0 12px 26px ${alpha(DASHBOARD_ACCENT, 0.32)}, 0 0 0 1.5px ${alpha(DASHBOARD_ACCENT, 0.85)}, inset 0 1px 0 ${alpha('#fff', 0.6)}`)
                    : chipShadowHover,
                  borderColor: isRoomMoveHighlighted
                    ? alpha(DASHBOARD_ACCENT, isDarkMode ? 0.98 : 0.9)
                    : alpha(accent, isDarkMode ? 0.55 : 0.48),
                },
              }
            : {},
        }}
      >
        {isBlockBar ? (
          <Box
            className="booking-bar-pill"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 0.4,
              pl: 0.5,
              pr: 0.6,
              boxSizing: 'border-box',
              background: chipBg,
              border: chipBorder,
              borderRadius: pillBorderRadius,
              boxShadow: chipShadow,
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease',
            }}
          >
            <BlockIcon sx={{ fontSize: '0.9rem', color: labelColor, flexShrink: 0, opacity: 0.95 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: labelColor,
                fontSize: labelFontSize,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                letterSpacing: '0.02em',
              }}
            >
              {bar.blockInfo?.reason || bar.booking?.guest_name || 'Blocked'}
            </Typography>
          </Box>
        ) : isWebCheckInHold ? (
          <Box
            className="booking-bar-pill"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 0.4,
              pl: 0.5,
              pr: showMenuButton ? 0.2 : 0.6,
              boxSizing: 'border-box',
              background: chipBg,
              border: chipBorder,
              borderRadius: pillBorderRadius,
              boxShadow: chipShadow,
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease',
            }}
          >
            <HowToRegIcon sx={{ fontSize: '0.95rem', color: labelColor, flexShrink: 0, opacity: 0.95 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: labelColor,
                fontSize: labelFontSize,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                letterSpacing: '0.02em',
              }}
            >
              {bar.webCheckInInvite?.guest_name || 'Web Check-in'}
            </Typography>
            {showMenuButton ? (
              <IconButton
                size="small"
                aria-label="Web check-in actions"
                onClick={handleMenuOpen}
                sx={menuButtonSx}
              >
                <MoreVertIcon sx={{ fontSize: Math.max(13, Math.min(15, barHeight - 16)) }} />
              </IconButton>
            ) : null}
          </Box>
        ) : bar.booking ? (
          <Box
            className="booking-bar-pill"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 0.4,
              pl: 0.4,
              pr: showMenuButton ? 0.2 : 0.55,
              boxSizing: 'border-box',
              background: chipBg,
              border: chipBorder,
              borderRadius: pillBorderRadius,
              boxShadow: chipShadow,
              overflow: 'hidden',
              isolation: 'isolate',
              // Avoid backdrop-filter blur here — it tanks scroll FPS on dense Ultra boards.
              transition: 'box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
            }}
          >
            <Box
              data-room-move-anchor="source"
              sx={{
                width: logoSizePx,
                height: logoSizePx,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: '4px',
              }}
            >
              <BookingSourceLogo
                channelImagePath={bar.booking.channel_image_path}
                channelName={bar.booking.channel_name}
                isDarkMode={isDarkMode}
                sizePx={logoSizePx}
              />
            </Box>

            {isGroupMember ? (
              <Box
                title={groupBadgeTitle}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: Math.max(14, Math.min(18, logoSizePx)),
                  height: Math.max(14, Math.min(18, logoSizePx)),
                  borderRadius: '50%',
                  bgcolor: isDarkMode ? alpha('#0f172a', 0.85) : alpha('#ffffff', 0.92),
                  border: `1.5px solid ${alpha(DASHBOARD_ACCENT, 0.85)}`,
                  pointerEvents: 'none',
                }}
              >
                <GroupsIcon
                  sx={{
                    fontSize: Math.max(10, Math.min(12, logoSizePx - 4)),
                    color: DASHBOARD_ACCENT,
                  }}
                />
              </Box>
            ) : null}

            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: labelColor,
                fontSize: labelFontSize,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                letterSpacing: '0.02em',
                textShadow: isDarkMode
                  ? '0 1px 2px rgba(0,0,0,0.55)'
                  : '0 1px 0 rgba(255,255,255,0.35)',
                ...(isCancelledLike && {
                  textDecoration: 'line-through',
                  opacity: 0.88,
                }),
              }}
            >
              {bar.booking.guest_name}
            </Typography>

            {showRoomChangeIcon ? (
              <RoomMoveIconTooltip
                title={roomChangeTooltipTitle}
                isDarkMode={isDarkMode}
                accent="#fb923c"
              >
                <IconButton
                  size="small"
                  aria-label="Room change"
                  data-room-move-anchor="change"
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setSuppressBarTooltip(true)}
                  onMouseLeave={() => setSuppressBarTooltip(false)}
                  sx={moveIconBtnSx('#fb923c')}
                >
                  <RoomChangeIcon sx={{ fontSize: Math.max(11, Math.min(13, barHeight - 18)) }} />
                </IconButton>
              </RoomMoveIconTooltip>
            ) : null}

            {showRoomUpgradeIcon ? (
              <RoomMoveIconTooltip
                title={roomUpgradeTooltipTitle}
                isDarkMode={isDarkMode}
                accent={roomTypeChangeAccent}
              >
                <IconButton
                  size="small"
                  aria-label={isRoomTypeDowngrade ? 'Room downgrade' : 'Room upgrade'}
                  data-room-move-anchor={isRoomTypeDowngrade ? 'downgrade' : 'upgrade'}
                  onClick={(e) => e.stopPropagation()}
                  onMouseEnter={() => setSuppressBarTooltip(true)}
                  onMouseLeave={() => setSuppressBarTooltip(false)}
                  sx={moveIconBtnSx(roomTypeChangeAccent)}
                >
                  {isRoomTypeDowngrade ? (
                    <RoomDowngradeIcon sx={{ fontSize: Math.max(11, Math.min(13, barHeight - 18)) }} />
                  ) : (
                    <RoomUpgradeIcon sx={{ fontSize: Math.max(11, Math.min(13, barHeight - 18)) }} />
                  )}
                </IconButton>
              </RoomMoveIconTooltip>
            ) : null}

            {showMenuButton ? (
              <IconButton
                size="small"
                aria-label="Booking actions"
                onClick={handleMenuOpen}
                sx={menuButtonSx}
              >
                <MoreVertIcon sx={{ fontSize: Math.max(13, Math.min(15, barHeight - 16)) }} />
              </IconButton>
            ) : null}
          </Box>
        ) : null}
      </Box>
  );

  return (
    <>
      {bookingTooltipTitle ? (
        <Tooltip
          title={bookingTooltipTitle}
          arrow
          placement="top"
          enterDelay={280}
          leaveDelay={60}
          describeChild
          disableInteractive={false}
          open={menuOpen || suppressBarTooltip ? false : undefined}
          onOpen={() => setBarTooltipOpen(true)}
          onClose={() => setBarTooltipOpen(false)}
          slotProps={{
            popper: {
              modifiers: [
                { name: 'offset', options: { offset: [0, -2] } },
                { name: 'preventOverflow', options: { padding: 8 } },
              ],
            },
            tooltip: { sx: barTooltipPaperSx(isDarkMode, accent) },
            arrow: {
              sx: {
                color: isDarkMode ? alpha('#020617', 0.96) : alpha('#ffffff', 0.98),
              },
            },
          }}
        >
          {barNode}
        </Tooltip>
      ) : (
        barNode
      )}

      {showActionsMenu ? (
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 168,
                borderRadius: '12px',
                bgcolor: isDarkMode ? 'rgba(3,7,18,0.96)' : '#fff',
                border: `1px solid ${isDarkMode ? alpha('#ffffff', 0.12) : alpha('#000', 0.1)}`,
                color: isDarkMode ? '#f8fafc' : '#0f172a',
                '& .MuiMenuItem-root': {
                  color: isDarkMode ? '#f8fafc' : '#0f172a',
                },
              },
            },
          }}
        >
          {isWebCheckInHold ? (
            <>
              {showWebCheckInMenu ? (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    openWebCheckInInvite();
                  }}
                >
                  <ListItemIcon>
                    <HowToRegIcon fontSize="small" sx={{ color: WEB_CHECKIN_ACCENT }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Web Check-in"
                    secondary={webCheckInCountdown ? `Expires ${webCheckInCountdown}` : undefined}
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                  />
                </MenuItem>
              ) : null}
              {hasMaintenance ? (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    navigate('/housekeeping/maintenance/');
                  }}
                >
                  <ListItemIcon>
                    <BuildIcon fontSize="small" sx={{ color: '#ef5350' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Maintenance"
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  />
                </MenuItem>
              ) : null}
            </>
          ) : (
            <>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openViewBooking();
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                </ListItemIcon>
                <ListItemText
                  primary="View Booking"
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                />
              </MenuItem>
              {checkInEligible ? (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    onCheckIn?.(bookingId);
                  }}
                >
                  <ListItemIcon>
                    <LoginIcon fontSize="small" sx={{ color: '#60a5fa' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Check-in"
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  />
                </MenuItem>
              ) : null}
              {checkoutEligible ? (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    onCheckout?.(bookingId);
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: '#4ce1b6' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Checkout"
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  />
                </MenuItem>
              ) : null}
              {hasMaintenance ? (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    navigate('/housekeeping/maintenance/');
                  }}
                >
                  <ListItemIcon>
                    <BuildIcon fontSize="small" sx={{ color: '#ef5350' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Maintenance"
                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                  />
                </MenuItem>
              ) : null}
            </>
          )}
        </Menu>
      ) : null}
    </>
  );
};

function bookingBarPropsAreEqual(prev, next) {
  return (
    prev.bar === next.bar
    && prev.room === next.room
    && prev.dateRangeLength === next.dateRangeLength
    && prev.isDarkMode === next.isDarkMode
    && prev.roomColumnWidth === next.roomColumnWidth
    && prev.laneIndex === next.laneIndex
    && prev.barHeight === next.barHeight
    && prev.barGap === next.barGap
    && prev.barTopOffset === next.barTopOffset
    && prev.layoutVariant === next.layoutVariant
    && prev.barMetrics === next.barMetrics
    && prev.onClick === next.onClick
    && prev.onViewBooking === next.onViewBooking
    && prev.onOpenWebCheckInInvite === next.onOpenWebCheckInInvite
    && prev.onCheckIn === next.onCheckIn
    && prev.onCheckout === next.onCheckout
  );
}

export default memo(BookingBar, bookingBarPropsAreEqual);
